/**
 * Address validation utility using Google Maps Geocoding API
 */

/**
 * Validates an address using Google Maps Geocoding API
 * @param address - The address string to validate
 * @returns Promise that resolves to coordinates if valid, null if invalid
 */
export const validateAddress = async (
  address: string,
): Promise<{ lat: number; lng: number } | null> => {
  if (!window.google || !address || !address.trim()) {
    return null;
  }

  const geocoder = new google.maps.Geocoder();
  return new Promise((resolve) => {
    geocoder.geocode({ address: address.trim() }, (results, status) => {
      if (
        status === google.maps.GeocoderStatus.OK &&
        results &&
        results.length > 0
      ) {
        const filtered = results.filter((r) => {
          const countryComponent = r.address_components.find((c) =>
            c.types.includes("country"),
          );
          if (!countryComponent) return false;
          const code = countryComponent.short_name;
          return ["US", "CA", "MX"].includes(code);
        });
        const best = filtered[0] || results[0]; // fallback if none matched
        const location = best.geometry.location;
        const partialMatch = best.partial_match || false;
        const types = best.types || [];
        const locationType = best.geometry.location_type; // 'ROOFTOP', 'APPROXIMATE', etc.

        // 1. Identify if the result is just a high-level region (State/Country)
        const broadTypes = [
          "country",
          "administrative_area_level_1",
          "administrative_area_level_2",
        ];
        const isJustARegion = types.every((t) => broadTypes.includes(t));

        // 2. Define our "Hard Reject" criteria
        // We reject if Google says it's a partial match AND the result is just a State/Country
        if (partialMatch && isJustARegion) {
          //console.log("[Address Validation] REJECTED - Result too broad");
          resolve(null);
          return;
        }

        // 3. Define our "Soft Success" criteria
        // If it's a ROOFTOP or a specific street address/postal code, it's 100% valid for a map.
        const isMapReady =
          locationType === "ROOFTOP" ||
          locationType === "RANGE_INTERPOLATED" ||
          locationType === "GEOMETRIC_CENTER" ||
          types.includes("street_address") ||
          types.includes("postal_code") ||
          types.includes("premise");

        if (isMapReady) {
          // console.log(
          //   "[Address Validation] ACCEPTED - Address geocoded successfully",
          //   {
          //     address: address.trim(),
          //     partialMatch,
          //     types,
          //   }
          // );
          resolve({ lat: location.lat(), lng: location.lng() });
        } else {
          // If it's something like 'locality' (City center), decide if you want to allow it
          // resolve({ lat: location.lat(), lng: location.lng() });
          resolve(null);
        }
      }
    });
  });
};

/**
 * Validates multiple addresses and returns a map of valid addresses with coordinates
 * @param addresses - Map of work order IDs to addresses
 * @returns Promise that resolves to a map of valid addresses with their coordinates
 */
export const validateAddresses = async (
  addresses: Map<number, string>,
): Promise<Map<number, { lat: number; lng: number }>> => {
  const validAddresses = new Map<number, { lat: number; lng: number }>();
  //debugger;
  if (!window.google || addresses.size === 0) {
    return validAddresses;
  }

  // Validate all addresses in parallel
  const validationPromises = Array.from(addresses.entries()).map(
    async ([id, address]) => {
      const coordinates = await validateAddress(address);
      if (coordinates) {
        return { id, coordinates };
      }
      return null;
    },
  );

  const results = await Promise.all(validationPromises);

  // Build the map of valid addresses
  results.forEach((result) => {
    if (result) {
      validAddresses.set(result.id, result.coordinates);
    }
  });

  return validAddresses;
};
